import React, { useCallback } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';

import { READ_RECEIPT_TIMEOUT } from '@web/features/staking/utils';
import { Button } from '@web/ui/components';
import { IModalProps } from '@web/ui/components/modal';
import { env } from '@web/utils/env';
import { clients } from '@web/utils/rainbowkit/chains';

import { useApprovalForAll, useStakeAll } from '../hooks';

import { BaseStakingModal } from './BaseStakingModal';
import { StakingAlert } from './components/StakingAlert';
import { StakingStep } from './components/StakingStep';
import { getModalStatus } from './getModalStatus';

const Action = styled(Button)`
  white-space: nowrap;
`;

export interface IStakeModalProps extends IModalProps {
  personas: IPersonaFront[];
}

export const StakeModal = ({
  personas,
  onClose,
  ...props
}: IStakeModalProps) => {
  const t = useTranslations();

  const { mutation: approvalMutation } = useApprovalForAll();
  const stakeAll = useStakeAll();

  const handleStake = useCallback(async () => {
    const approvalHash = await approvalMutation.mutateAsync({
      blockchain: Blockchain.BASE,
      operator: env.base.STAKING_ADDRESS,
    });

    if (approvalHash) {
      await clients.base.waitForTransactionReceipt({
        hash: approvalHash,
        timeout: READ_RECEIPT_TIMEOUT,
      });
    }

    await stakeAll.mutation.mutateAsync({
      tokenIds: personas.map((i) => i.tokenId),
    });
  }, [personas]);

  const handleClose = () => {
    stakeAll.mutation.reset();
    onClose();
  };

  const status = getModalStatus([
    stakeAll.mutation.status,
    stakeAll.receipt.status,
  ]);

  return (
    <BaseStakingModal
      personas={personas}
      status={status}
      title={t('STAKE_MODAL_TITLE_BASE', {
        personas: personas.length,
      })}
      subtitle={t('STAKE_MODAL_SUBTITLE')}
      completedText={t('STAKE_MODAL_COMPLETED_TEXT')}
      onClose={handleClose}
      actions={[
        <Action
          label={t('STAKE_MODAL_ACTION_CONFIRM_BASE')}
          onClick={handleStake}
          fullWidth
        />,
        <Action
          label={t('STAKE_MODAL_ACTION_CANCEL')}
          onClick={handleClose}
          variant="secondary"
          color="onSurface"
          background="surface"
        />,
      ]}
      {...props}
    >
      {status === 'error' && (
        <StakingAlert>{t('STAKING_ALERT_ERROR')}</StakingAlert>
      )}
      <StakingStep
        title={t('STAKING_STEP_STAKE_TITLE')}
        description={t('STAKING_STEP_STAKE_DESCRIPTION')}
        status={status}
        type="stake"
      />
    </BaseStakingModal>
  );
};
